/**
 * useVersionCheck Hook
 *
 * This hook is responsible for ensuring that the client is running the latest version of the frontend.
 * It implements a reference-driven update model that works independently of the backend API.
 *
 * Mechanisms:
 * 1. Primary: Periodically fetches the `/version` endpoint, which serves a `version.json`
 *    file containing the current official version (generated during Docker build).
 * 2. Backup: Checks Firebase Remote Config for a `frontend_version` parameter. This allows for
 *    emergency manual overrides if the primary mechanism fails or if a specific version needs to be forced.
 *
 * Safety & UX:
 * - Checks are bypassed in 'local' environments to prevent constant reloads during development.
 * - If an update is detected, it checks if a recording is currently active (via audioStore).
 *   If the user is recording, the update is postponed to avoid data loss.
 * - When an update is triggered, a warning message is shown, and the page reloads after 10 seconds.
 * - After reload, a confirmation notification is shown to the user.
 */
import { useCallback, useEffect, useRef } from 'react'
import axios from 'axios'
import { showMessage } from '../utils/snackbar'
import { breadcrumb } from '../utils/sentry'
import { useAudioStore } from '../store/audioStore'
import { getEnvironment } from '../utils/environment'
import { isAxiosNetworkError, isAxiosServerError } from '../utils/axiosUtils'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '../store/appStore'

export const SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY = 'show_version_update_notification'
const clientVersion = import.meta.env.VITE_VERSION

const VERSION_CHECK_INTERVAL = 60_000 // 1 minute
const RELOAD_DELAY = 10_000 // 10 seconds

let reloadTriggered = false

export const reloadApp = () => {
    if (reloadTriggered) return
    reloadTriggered = true
    setTimeout(() => {
        const isRecording = !!useAudioStore.getState().mediaRecorder
        const isEditing = useAppStore.getState().isEditing
        if (isRecording || isEditing) {
            console.info('[VersionCheck] Reload postponed due to recording or editing.')
            reloadTriggered = false
            return
        }
        sessionStorage.setItem(SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY, 'true')
        window.location.reload()
    }, RELOAD_DELAY)
}

interface VersionResponse {
    version: string
}

export const useVersionCheck = () => {
    const { t } = useTranslation()
    const checkTimerRef = useRef<NodeJS.Timeout | null>(null)

    const triggerUpdate = useCallback(
        (targetVersion: string) => {
            const isRecording = !!useAudioStore.getState().mediaRecorder
            const isEditing = useAppStore.getState().isEditing

            if (isRecording || isEditing) {
                console.info('[VersionCheck] Reload postponed due to recording or editing.')
                return
            }

            console.info('[VersionCheck] Reloading page due to version update.', { clientVersion, targetVersion })
            breadcrumb('version', 'Reloading page due to version update', { clientVersion, targetVersion })

            showMessage(t('common.new_version_available'), 'warning', RELOAD_DELAY)
            reloadApp()
        },
        [t]
    )

    const checkRemoteFrontendVersion = useCallback(async () => {
        if (getEnvironment() === 'local') return
        // Primary mechanism: check /version endpoint

        try {
            const url = `${window.location.origin}/version`
            console.info(`[VersionCheck] Checking for updates: ${url}`)
            const response = await axios.get<VersionResponse>(url, {
                headers: { 'Cache-Control': 'no-cache' },
            })
            const remoteVersion = response.data.version

            if (remoteVersion && remoteVersion !== clientVersion) {
                console.info('[VersionCheck] Update available via /version.', { clientVersion, remoteVersion })
                triggerUpdate(remoteVersion)
            }
        } catch (error: unknown) {
            // 5xx responses (e.g. 503 during deploys) are transient: the check retries every
            // minute, so they are logged as warnings instead of Sentry-captured errors.
            const isTransient =
                isAxiosNetworkError(error) ||
                isAxiosServerError(error) ||
                (error instanceof Error && error.message.includes('timeout'))

            if (isTransient) {
                console.warn('[VersionCheck] Transient error while fetching version:', error)
            } else {
                console.error('[VersionCheck] Failed to fetch version:', error)
            }
        }
    }, [triggerUpdate])

    useEffect(() => {
        // Initial check on mount
        checkRemoteFrontendVersion()

        // Set up periodic check
        checkTimerRef.current = setInterval(() => {
            checkRemoteFrontendVersion()
        }, VERSION_CHECK_INTERVAL)

        return () => {
            if (checkTimerRef.current) {
                clearInterval(checkTimerRef.current)
            }
        }
    }, [checkRemoteFrontendVersion])
}
